import React, { useState, useEffect } from "react";

import { makeStyles, alpha, useTheme } from "@material-ui/core/styles";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  IconButton,
  Tooltip,
  Typography,
  CircularProgress,
  Popover,
} from "@material-ui/core";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import DragIndicatorIcon from "@material-ui/icons/DragIndicator";
import FlashOnIcon from "@material-ui/icons/FlashOn";
import ArrowUpwardIcon from "@material-ui/icons/ArrowUpward";
import ArrowDownwardIcon from "@material-ui/icons/ArrowDownward";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import AddIcon from "@material-ui/icons/Add";
import { toast } from "react-toastify";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { FormDialogHeader, FormSection, useFormDialogStyles } from "../FormDialog";

const STAGE_TYPES = [
  { value: "open", label: "Em andamento", color: "#1E88E5" },
  { value: "won", label: "Ganho", color: "#43A047" },
  { value: "lost", label: "Perdido", color: "#E53935" },
];

const PALETTE = [
  "#6AA5F5", "#1E88E5", "#26C6DA", "#00897B", "#43A047", "#9CCC65",
  "#FFB547", "#FB8C00", "#E53935", "#F48FB1", "#B388FF", "#7E57C2",
  "#8D6E63", "#90A4AE",
];

const DEFAULT_COLOR = "#90A4AE";

const useStyles = makeStyles((theme) => ({
  preview: {
    display: "flex",
    gap: 4,
  },
  previewLane: {
    minWidth: 0,
    height: 30,
    padding: theme.spacing(0, 1.25),
    boxSizing: "border-box",
    borderRadius: 6,
    backgroundColor: theme.palette.action.hover,
    fontSize: 12,
    fontWeight: 600,
    display: "flex",
    alignItems: "center",
    overflow: "hidden",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
  },
  stage: {
    borderRadius: 12,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.action.hover,
    overflow: "hidden",
    transition: "opacity .12s, border-color .12s",
  },
  stageDragging: {
    opacity: 0.45,
  },
  stageDropTarget: {
    borderColor: theme.palette.primary.main,
    boxShadow: `0 0 0 1px ${theme.palette.primary.main}`,
  },
  stageRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(1, 1, 1, 0.5),
    [theme.breakpoints.down("xs")]: { flexWrap: "wrap" },
  },
  handle: {
    cursor: "grab",
    color: theme.palette.text.disabled,
    display: "inline-flex",
    "&:active": { cursor: "grabbing" },
  },
  colorButton: {
    width: 26,
    height: 26,
    borderRadius: 8,
    border: `2px solid ${alpha(theme.palette.text.primary, 0.15)}`,
    cursor: "pointer",
    flexShrink: 0,
    padding: 0,
  },
  nameInput: {
    flex: 1,
    minWidth: 140,
    "& .MuiOutlinedInput-root": { borderRadius: 8 },
    "& input": { padding: "8px 12px" },
  },
  segment: {
    display: "flex",
    borderRadius: 8,
    overflow: "hidden",
    border: `1px solid ${theme.palette.divider}`,
    flexShrink: 0,
  },
  segmentButton: {
    height: 32,
    padding: theme.spacing(0, 1.25),
    border: 0,
    cursor: "pointer",
    font: "600 12px Inter, Roboto, sans-serif",
    whiteSpace: "nowrap",
    backgroundColor: "transparent",
    color: theme.palette.text.secondary,
    "&:focus-visible": { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: -2 },
  },
  taskSummary: {
    padding: theme.spacing(0, 1.5, 1.25, 8.5),
    fontSize: 12,
    color: theme.palette.text.secondary,
    [theme.breakpoints.down("xs")]: { paddingLeft: theme.spacing(1.5) },
  },
  taskPanel: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) 160px",
    gap: theme.spacing(1.5),
    padding: theme.spacing(1.5),
    borderTop: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
    [theme.breakpoints.down("xs")]: { gridTemplateColumns: "minmax(0, 1fr)" },
  },
  swatches: {
    display: "grid",
    gridTemplateColumns: "repeat(7, 28px)",
    gap: 8,
    padding: theme.spacing(1.5),
  },
  swatch: {
    width: 28,
    height: 28,
    borderRadius: 8,
    border: 0,
    cursor: "pointer",
    padding: 0,
  },
  addButton: {
    height: 42,
    borderRadius: 10,
    borderStyle: "dashed",
    textTransform: "none",
    fontWeight: 500,
    color: theme.palette.text.secondary,
    borderColor: theme.palette.divider,
  },
  info: {
    display: "flex",
    gap: theme.spacing(1),
    padding: theme.spacing(1.25, 1.5),
    borderRadius: 10,
    fontSize: 13,
    lineHeight: "19px",
    color: theme.palette.text.secondary,
    backgroundColor: alpha(theme.palette.primary.main, 0.08),
    border: `1px solid ${alpha(theme.palette.primary.main, 0.25)}`,
  },
}));

// chave estável para raias ainda não salvas (o índice muda ao reordenar)
let tempKeySeq = 0;
const nextTempKey = () => {
  tempKeySeq += 1;
  return `novo-${tempKeySeq}`;
};

const toFormStage = (stage) => ({
  key: String(stage.id),
  id: stage.id,
  name: stage.name || "",
  color: stage.color || DEFAULT_COLOR,
  type: stage.type || "open",
  taskTemplate: stage.taskTemplate || "",
  taskDueDays: stage.taskDueDays == null ? "" : String(stage.taskDueDays),
});

const dueLabel = (days) => {
  if (days === "" || days == null) return "sem prazo";
  const n = Number(days);
  return n === 1 ? "prazo de 1 dia" : `prazo de ${n} dias`;
};

const PipelineModal = ({ open, onClose, pipeline }) => {
  const classes = useStyles();
  const dialogClasses = useFormDialogStyles();
  const theme = useTheme();

  const [name, setName] = useState("");
  const [stages, setStages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedKey, setExpandedKey] = useState(null);
  const [colorAnchor, setColorAnchor] = useState(null); // { el, key }
  const [dragKey, setDragKey] = useState(null);
  const [overKey, setOverKey] = useState(null);

  useEffect(() => {
    if (!open || !pipeline) return;
    setName(pipeline.name || "");
    setStages(
      (pipeline.stages || [])
        .slice()
        .sort((a, b) => a.order - b.order)
        .map(toFormStage)
    );
    setExpandedKey(null);
  }, [open, pipeline]);

  const setStageField = (key, field, value) => {
    setStages((prev) =>
      prev.map((stage) => (stage.key === key ? { ...stage, [field]: value } : stage))
    );
  };

  const addStage = () => {
    setStages((prev) => {
      const next = prev.slice();
      // etapa nova entra antes das de fechamento (Ganho/Perdido)
      const firstClosed = next.findIndex((s) => s.type !== "open");
      const stage = {
        key: nextTempKey(),
        name: "",
        color: PALETTE[next.length % PALETTE.length],
        type: "open",
        taskTemplate: "",
        taskDueDays: "",
      };
      next.splice(firstClosed < 0 ? next.length : firstClosed, 0, stage);
      return next;
    });
  };

  const removeStage = (key) => {
    setStages((prev) => prev.filter((stage) => stage.key !== key));
  };

  const moveStageTo = (fromKey, toKey) => {
    if (!fromKey || fromKey === toKey) return;
    setStages((prev) => {
      const from = prev.findIndex((s) => s.key === fromKey);
      const to = prev.findIndex((s) => s.key === toKey);
      if (from < 0 || to < 0) return prev;
      const next = prev.slice();
      const [item] = next.splice(from, 1);
      next.splice(to, 0, item);
      return next;
    });
  };

  const moveStageBy = (index, delta) => {
    const target = index + delta;
    if (target < 0 || target >= stages.length) return;
    moveStageTo(stages[index].key, stages[target].key);
  };

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error("Informe o nome do funil.");
      return;
    }
    if (stages.length === 0) {
      toast.error("O funil precisa de pelo menos uma etapa.");
      return;
    }
    if (stages.some((stage) => !stage.name.trim())) {
      toast.error("Todas as etapas precisam de um nome.");
      return;
    }
    if (!stages.some((stage) => stage.type === "open")) {
      toast.error(
        "O funil precisa de pelo menos uma etapa Em andamento para receber novos negócios."
      );
      return;
    }

    setLoading(true);
    try {
      // a API trata a lista como a verdade do funil: etapa existente que
      // não vier aqui é excluída, por isso enviamos sempre tudo
      const payload = {
        name: name.trim(),
        stages: stages.map((stage) => ({
          id: stage.id,
          name: stage.name.trim(),
          color: stage.color,
          type: stage.type,
          taskTemplate: stage.taskTemplate.trim() || null,
          taskDueDays: stage.taskDueDays === "" ? null : Number(stage.taskDueDays),
        })),
      };

      await api.put(`/pipelines/${pipeline.id}`, payload);
      toast.success("Funil atualizado.");
      onClose(true);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => onClose()}
      maxWidth="md"
      fullWidth
      scroll="paper"
      classes={{ paper: dialogClasses.paper }}
      aria-labelledby="pipeline-title"
    >
      <FormDialogHeader
        id="pipeline-title"
        title="Configurar funil"
        subtitle="Defina as etapas, a ordem e o que acontece quando um negócio entra em cada uma."
        onClose={() => onClose()}
        disabled={loading}
      />

      <DialogContent dividers className={dialogClasses.content}>
        <FormSection title="Funil">
          <TextField
            label="Nome do funil"
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            variant="outlined"
            margin="dense"
            InputLabelProps={{ shrink: true }}
          />
          <span className={dialogClasses.muted} style={{ fontSize: 12 }}>
            Prévia das colunas no quadro:
          </span>
          <div className={classes.preview}>
            {stages.map((stage) => (
              <span
                key={stage.key}
                className={classes.previewLane}
                style={{
                  flex: stage.type === "open" ? "1 1 0" : "0 0 auto",
                  borderTop: `3px solid ${stage.color}`,
                }}
              >
                {stage.name || "Sem nome"}
              </span>
            ))}
          </div>
        </FormSection>

        <FormSection
          title="Etapas"
          action={
            <span className={dialogClasses.muted} style={{ fontSize: 12 }}>
              Arraste pela alça para reordenar
            </span>
          }
        >
          {stages.map((stage, index) => {
            const expanded = expandedKey === stage.key;
            return (
              <div
                key={stage.key}
                className={`${classes.stage} ${dragKey === stage.key ? classes.stageDragging : ""} ${
                  overKey === stage.key && dragKey && dragKey !== stage.key ? classes.stageDropTarget : ""
                }`}
                onDragOver={(e) => {
                  if (!dragKey) return;
                  e.preventDefault();
                  if (overKey !== stage.key) setOverKey(stage.key);
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  moveStageTo(dragKey, stage.key);
                  setDragKey(null);
                  setOverKey(null);
                }}
              >
                <div className={classes.stageRow}>
                  <span
                    className={classes.handle}
                    draggable
                    title="Arrastar para reordenar"
                    onDragStart={(e) => {
                      setDragKey(stage.key);
                      e.dataTransfer.effectAllowed = "move";
                      // Firefox exige algum dado para iniciar o arraste
                      e.dataTransfer.setData("text/plain", stage.key);
                    }}
                    onDragEnd={() => {
                      setDragKey(null);
                      setOverKey(null);
                    }}
                  >
                    <DragIndicatorIcon fontSize="small" />
                  </span>
                  <Tooltip title="Cor da etapa">
                    <button
                      type="button"
                      className={classes.colorButton}
                      style={{ backgroundColor: stage.color }}
                      aria-label="Cor da etapa"
                      onClick={(e) => setColorAnchor({ el: e.currentTarget, key: stage.key })}
                    />
                  </Tooltip>
                  <TextField
                    className={classes.nameInput}
                    value={stage.name}
                    onChange={(e) => setStageField(stage.key, "name", e.target.value)}
                    variant="outlined"
                    size="small"
                    placeholder="Nome da etapa"
                    inputProps={{ "aria-label": "Nome da etapa" }}
                  />
                  <div className={classes.segment} role="group" aria-label="Tipo da etapa">
                    {STAGE_TYPES.map((type) => {
                      const active = stage.type === type.value;
                      return (
                        <button
                          key={type.value}
                          type="button"
                          className={classes.segmentButton}
                          aria-pressed={active}
                          onClick={() => setStageField(stage.key, "type", type.value)}
                          style={
                            active
                              ? { backgroundColor: type.color, color: "#fff" }
                              : undefined
                          }
                        >
                          {type.label}
                        </button>
                      );
                    })}
                  </div>
                  <Tooltip title="Tarefa automática">
                    <IconButton
                      size="small"
                      onClick={() => setExpandedKey(expanded ? null : stage.key)}
                      aria-label="Tarefa automática"
                      aria-expanded={expanded}
                      style={{
                        color: stage.taskTemplate ? theme.palette.warning.main : undefined,
                      }}
                    >
                      <FlashOnIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Mover para cima">
                    <span>
                      <IconButton
                        size="small"
                        onClick={() => moveStageBy(index, -1)}
                        disabled={index === 0}
                        aria-label="Mover para cima"
                      >
                        <ArrowUpwardIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Mover para baixo">
                    <span>
                      <IconButton
                        size="small"
                        onClick={() => moveStageBy(index, 1)}
                        disabled={index === stages.length - 1}
                        aria-label="Mover para baixo"
                      >
                        <ArrowDownwardIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Remover etapa">
                    <IconButton size="small" onClick={() => removeStage(stage.key)} aria-label="Remover etapa">
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </div>

                {!expanded && stage.taskTemplate && (
                  <div className={classes.taskSummary}>
                    Cria tarefa: “{stage.taskTemplate}” · {dueLabel(stage.taskDueDays)}
                  </div>
                )}

                {expanded && (
                  <div className={classes.taskPanel}>
                    <TextField
                      label="Tarefa automática ao entrar nesta etapa"
                      value={stage.taskTemplate}
                      onChange={(e) => setStageField(stage.key, "taskTemplate", e.target.value)}
                      fullWidth
                      variant="outlined"
                      size="small"
                      placeholder="Ex.: Enviar proposta para {contato}"
                      helperText="Vazio = não cria tarefa. Variáveis: {contato} e {negocio}."
                      InputLabelProps={{ shrink: true }}
                      autoFocus
                    />
                    <TextField
                      label="Prazo (dias)"
                      type="number"
                      value={stage.taskDueDays}
                      onChange={(e) => setStageField(stage.key, "taskDueDays", e.target.value)}
                      fullWidth
                      variant="outlined"
                      size="small"
                      inputProps={{ min: 0 }}
                      helperText="Vazio = sem prazo."
                      InputLabelProps={{ shrink: true }}
                    />
                  </div>
                )}
              </div>
            );
          })}

          <Button variant="outlined" className={classes.addButton} startIcon={<AddIcon />} onClick={addStage}>
            Adicionar etapa
          </Button>

          <div className={classes.info}>
            <InfoOutlinedIcon fontSize="small" style={{ flexShrink: 0, marginTop: 1 }} />
            <Typography variant="body2" component="span" style={{ fontSize: 13 }}>
              Etapas <strong>Ganho</strong> e <strong>Perdido</strong> fecham o negócio; ao mover
              para Perdido, o motivo é obrigatório. Use <strong>Ganho</strong> também para etapas
              de pós-venda, assim a venda continua contando no relatório. Uma etapa só pode ser
              excluída se não tiver nenhum negócio.
            </Typography>
          </div>
        </FormSection>
      </DialogContent>

      <Popover
        open={Boolean(colorAnchor)}
        anchorEl={colorAnchor?.el}
        onClose={() => setColorAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        <div className={classes.swatches}>
          {PALETTE.map((color) => (
            <button
              key={color}
              type="button"
              className={classes.swatch}
              style={{ backgroundColor: color }}
              aria-label={color}
              onClick={() => {
                setStageField(colorAnchor.key, "color", color);
                setColorAnchor(null);
              }}
            />
          ))}
        </div>
      </Popover>

      <DialogActions className={dialogClasses.actions}>
        <Button
          onClick={() => onClose()}
          disabled={loading}
          className={`${dialogClasses.actionButton} ${dialogClasses.cancelButton}`}
        >
          Cancelar
        </Button>
        <Button
          onClick={handleSave}
          color="primary"
          variant="contained"
          disableElevation
          disabled={loading}
          className={`${dialogClasses.actionButton} ${dialogClasses.progressWrapper}`}
        >
          Salvar funil
          {loading && <CircularProgress size={24} className={dialogClasses.buttonProgress} />}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PipelineModal;
